import React, { useState, useEffect } from "react";
import { MdWarningAmber, MdCheckCircle, MdErrorOutline, MdInfoOutline, MdClose, MdContentCopy } from "react-icons/md";
import "./ConfirmModal.css";

/**
 * AWS-style Confirmation Modal
 * 
 * Usage:
 *   <ConfirmModal
 *     show={showModal}
 *     title="Block IP Address"
 *     message="Are you sure you want to block this IP?"
 *     detail="This IP will be blocked from accessing the entire server."
 *     confirmText="Block IP"
 *     cancelText="Cancel"
 *     variant="danger"        // "danger" | "warning" | "success" | "info"
 *     confirmPhrase="confirm" // User must type this to enable the button (optional)
 *     onConfirm={handleConfirm}
 *     onCancel={() => setShowModal(false)}
 *   />
 */
const VARIANT_CONFIG = {
  danger: {
    icon: MdErrorOutline,
    iconBg: "var(--confirm-danger-bg, #fef2f2)",
    iconColor: "var(--confirm-danger-color, #dc2626)",
    btnClass: "confirm-btn-danger",
  },
  warning: {
    icon: MdWarningAmber,
    iconBg: "var(--confirm-warning-bg, #fffbeb)",
    iconColor: "var(--confirm-warning-color, #d97706)",
    btnClass: "confirm-btn-warning",
  },
  success: {
    icon: MdCheckCircle,
    iconBg: "var(--confirm-success-bg, #f0fdf4)",
    iconColor: "var(--confirm-success-color, #16a34a)",
    btnClass: "confirm-btn-success",
  },
  info: {
    icon: MdInfoOutline,
    iconBg: "var(--confirm-info-bg, #eff6ff)",
    iconColor: "var(--confirm-info-color, #2563eb)",
    btnClass: "confirm-btn-info",
  },
};

const ConfirmModal = ({
  show = false,
  title = "Confirm Action",
  message = "Are you sure you want to proceed?",
  detail = "",
  confirmText = "Confirm",
  cancelText = "Cancel",
  variant = "danger",
  confirmPhrase = "",        // e.g. "confirm" — user must type this to enable button
  onConfirm,
  onCancel,
  loading = false,
}) => {
  const [typedPhrase, setTypedPhrase] = useState("");
  const [copied, setCopied] = useState(false);

  // Reset typed phrase when modal opens/closes
  useEffect(() => {
    if (!show) {
      setTypedPhrase("");
      setCopied(false);
    }
  }, [show]);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(confirmPhrase);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!show) return null;

  const config = VARIANT_CONFIG[variant] || VARIANT_CONFIG.danger;
  const Icon = config.icon;

  // If confirmPhrase is set, button is disabled until the user types it exactly
  const phraseRequired = confirmPhrase.length > 0;
  const phraseMatches = typedPhrase.trim().toLowerCase() === confirmPhrase.toLowerCase();
  const isConfirmDisabled = loading || (phraseRequired && !phraseMatches);

  return (
    <div className="confirm-overlay" onClick={onCancel}>
      <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
        {/* Close button */}
        <button className="confirm-close-btn" onClick={onCancel} aria-label="Close">
          <MdClose />
        </button>

        {/* Icon + Content */}
        <div className="confirm-body">
          <div
            className="confirm-icon-wrapper"
            style={{ background: config.iconBg }}
          >
            <Icon style={{ color: config.iconColor, fontSize: "28px" }} />
          </div>
          <div className="confirm-content">
            <h5 className="confirm-title">{title}</h5>
            <p className="confirm-message">{message}</p>
            {detail && <p className="confirm-detail">{detail}</p>}
          </div>
        </div>

        {/* Confirmation phrase input (AWS-style) */}
        {phraseRequired && (
          <div className="confirm-phrase-section">
            <p className="confirm-phrase-label">
              To confirm, type <strong className="phrase-badge">{confirmPhrase}</strong> 
              <button 
                className={`copy-phrase-btn ${copied ? 'copied' : ''}`}
                onClick={copyToClipboard}
                title="Copy to clipboard"
                type="button"
              >
                {copied ? <span className="copied-text">Copied!</span> : <MdContentCopy />}
              </button>
              below:
            </p>
            <input
              type="text"
              className={`confirm-phrase-input ${phraseMatches ? 'valid' : ''}`}
              placeholder={confirmPhrase}
              value={typedPhrase}
              onChange={(e) => setTypedPhrase(e.target.value)}
              autoFocus
              spellCheck="false"
              autoComplete="off"
            />
          </div>
        )}

        {/* Actions */}
        <div className="confirm-actions">
          <button
            className="confirm-btn-cancel"
            onClick={onCancel}
            disabled={loading}
          >
            {cancelText}
          </button>
          <button
            className={`confirm-btn-action ${config.btnClass}`}
            onClick={onConfirm}
            disabled={isConfirmDisabled}
          >
            {loading ? (
              <span className="confirm-spinner"></span>
            ) : null}
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
